/**
 * TRUE_LIFETIME_CUSTOMER_LandingPage_v01
 * Site: TrueLifetimeCustomer.com
 * Purpose: Help businesses acquire, retain, and grow their customer base
 * Stack: React + TypeScript + React-Bootstrap + react-bootstrap-icons
 */

import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Nav,
  Navbar,
  Row,
} from "react-bootstrap";
import {
  ArrowRight,
  BarChart,
  ChatDots,
  People,
  StarFill,
  CheckCircleFill,
  ShieldCheck,
  GraphUpArrow,
} from "react-bootstrap-icons";

interface LeadFormState {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  website: string;
  monthlyCustomers: string;
  primaryGoal: string;
  notes: string;
  agree: boolean;
}

const defaultLeadFormState: LeadFormState = {
  fullName: "",
  email: "",
  phone: "",
  company: "",
  website: "",
  monthlyCustomers: "",
  primaryGoal: "",
  notes: "",
  agree: false,
};

export const TrueLifetimeCustomerLandingPage_2025_11_30_chat: React.FC = () => {
  const [leadForm, setLeadForm] = useState<LeadFormState>(defaultLeadFormState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target as HTMLInputElement;
    if (type === "checkbox") {
      setLeadForm((prev) => ({
        ...prev,
        [name]: (e.target as HTMLInputElement).checked,
      }));
    } else {
      setLeadForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = (): string | null => {
    if (!leadForm.fullName.trim()) return "Please enter your name.";
    if (!leadForm.email.trim()) return "Please enter your email address.";
    if (!leadForm.company.trim()) return "Please enter your company name.";
    if (!leadForm.primaryGoal.trim()) return "Please choose your primary goal.";
    if (!leadForm.agree)
      return "Please agree to be contacted so we can send your Customer Growth Plan.";
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    setSubmitting(true);

    // Simulate async submit. Replace with your API / Lambda / form handler.
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setLeadForm(defaultLeadFormState);
    }, 800);
  };

  return (
    <>
      <Helmet>
        <title>TrueLifetimeCustomer – Acquire, Retain & Grow Your Best Customers</title>
        <meta
          name="description"
          content="TrueLifetimeCustomer helps businesses acquire, retain, and grow their customer base with consulting, systems and tools like loyalty programs, referral engines, and retention playbooks."
        />
      </Helmet>

      {/* NAVBAR */}
      <Navbar bg="light" expand="lg" sticky="top" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#top" className="fw-bold">
            <span className="text-primary">True</span>
            <span className="text-dark">Lifetime</span>
            <span className="text-success">Customer</span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="tlc-navbar" />
          <Navbar.Collapse id="tlc-navbar">
            <Nav className="ms-auto">
              <Nav.Link href="#how-it-works">How It Works</Nav.Link>
              <Nav.Link href="#services">Services</Nav.Link>
              <Nav.Link href="#systems">Systems & Tools</Nav.Link>
              <Nav.Link href="#results">Results</Nav.Link>
              <Nav.Link href="#consult">Get a Plan</Nav.Link>
              <Button
                href="#consult"
                variant="success"
                className="ms-lg-3 mt-2 mt-lg-0"
              >
                Free Growth Plan
              </Button>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* HERO */}
      <section id="top" className="py-5 bg-light">
        <Container>
          <Row className="align-items-center">
            <Col lg={7} className="mb-4 mb-lg-0">
              <Badge bg="success" className="mb-3">
                Customer Lifetime Value • Retention • Loyalty
              </Badge>
              <h1 className="display-5 fw-bold mb-3">
                Turn One-Time Buyers into
                <span className="text-success"> True Lifetime Customers</span>
              </h1>
              <p className="lead text-muted mb-4">
                TrueLifetimeCustomer helps you design and implement customer
                acquisition, retention, and loyalty systems so every new customer
                becomes a long-term, profitable relationship.
              </p>
              <div className="d-flex flex-wrap gap-2 mb-3">
                <div className="d-flex align-items-center me-3">
                  <People className="me-2" />
                  <span>Acquire better-fit customers</span>
                </div>
                <div className="d-flex align-items-center me-3">
                  <StarFill className="me-2" />
                  <span>Increase loyalty & referrals</span>
                </div>
                <div className="d-flex align-items-center">
                  <GraphUpArrow className="me-2" />
                  <span>Grow Customer Lifetime Value</span>
                </div>
              </div>
              <div className="d-flex flex-wrap gap-3 align-items-center">
                <Button
                  href="#consult"
                  size="lg"
                  variant="success"
                  className="d-flex align-items-center"
                >
                  Get My Free Customer Growth Plan
                  <ArrowRight className="ms-2" />
                </Button>
                <Button href="#how-it-works" variant="outline-secondary">
                  See How It Works
                </Button>
              </div>
              <p className="text-muted small mt-3">
                No long-term contracts • Custom to your business • Quick wins in
                30–60 days
              </p>
            </Col>
            <Col lg={5}>
              <Card className="shadow border-0">
                <Card.Body>
                  <h5 className="fw-bold mb-2">
                    Get a 90-Day True Lifetime Customer Roadmap
                  </h5>
                  <p className="text-muted small mb-3">
                    Tell us about your customers and we’ll map out the fastest
                    path to higher retention, referrals, and lifetime value.
                  </p>

                  {error && (
                    <Alert variant="danger" className="py-2">
                      {error}
                    </Alert>
                  )}
                  {submitted && (
                    <Alert variant="success" className="py-2">
                      Thank you! Your request has been received. We’ll follow up
                      with your custom plan.
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Full Name *</Form.Label>
                      <Form.Control
                        type="text"
                        name="fullName"
                        value={leadForm.fullName}
                        onChange={handleChange}
                        placeholder="Jane Smith"
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Email *</Form.Label>
                      <Form.Control
                        type="email"
                        name="email"
                        value={leadForm.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Phone</Form.Label>
                      <Form.Control
                        type="tel"
                        name="phone"
                        value={leadForm.phone}
                        onChange={handleChange}
                        placeholder="(555) 555-5555"
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Company *</Form.Label>
                      <Form.Control
                        type="text"
                        name="company"
                        value={leadForm.company}
                        onChange={handleChange}
                        placeholder="Your Company Name"
                      />
                    </Form.Group>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">Website</Form.Label>
                      <InputGroup>
                        <InputGroup.Text>https://</InputGroup.Text>
                        <Form.Control
                          type="text"
                          name="website"
                          value={leadForm.website}
                          onChange={handleChange}
                          placeholder="example.com"
                        />
                      </InputGroup>
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">
                            Monthly Customers
                          </Form.Label>
                          <Form.Control
                            type="text"
                            name="monthlyCustomers"
                            value={leadForm.monthlyCustomers}
                            onChange={handleChange}
                            placeholder="e.g., 120"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-2">
                          <Form.Label className="small">
                            Primary Goal *
                          </Form.Label>
                          <Form.Select
                            name="primaryGoal"
                            value={leadForm.primaryGoal}
                            onChange={handleChange}
                          >
                            <option value="">Select a goal</option>
                            <option value="more-customers">
                              Acquire more new customers
                            </option>
                            <option value="retain-longer">
                              Keep customers longer
                            </option>
                            <option value="increase-spend">
                              Increase average spend
                            </option>
                            <option value="loyalty-program">
                              Build a loyalty / rewards program
                            </option>
                            <option value="referrals">
                              Generate more referrals & reviews
                            </option>
                            <option value="systems">
                              Build systems & automation
                            </option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-2">
                      <Form.Label className="small">
                        Anything else we should know?
                      </Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        name="notes"
                        value={leadForm.notes}
                        onChange={handleChange}
                        placeholder="Tell us about your best customers, or what’s not working today."
                      />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="tlc-agree">
                      <Form.Check
                        type="checkbox"
                        name="agree"
                        checked={leadForm.agree}
                        onChange={handleChange}
                        label={
                          <span className="small">
                            I agree to be contacted about my True Lifetime
                            Customer Growth Plan.
                          </span>
                        }
                      />
                    </Form.Group>
                    <Button
                      type="submit"
                      variant="success"
                      className="w-100 d-flex justify-content-center align-items-center"
                      disabled={submitting}
                    >
                      {submitting ? "Submitting..." : "Send My Growth Plan"}
                      <ArrowRight className="ms-2" />
                    </Button>
                    <p className="text-muted small mt-2 mb-0 text-center">
                      We respect your inbox—no spam, just ideas that grow your
                      customers.
                    </p>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-5">
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 className="fw-bold">How TrueLifetimeCustomer Works</h2>
              <p className="text-muted">
                We combine consulting, systems, and tools to build a predictable
                customer growth engine for your business.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="light" text="dark" className="mb-2">
                    Step 1
                  </Badge>
                  <h5 className="fw-bold mb-2">Customer Value Audit</h5>
                  <p className="text-muted small">
                    We analyze how customers find you, buy from you, return, and
                    refer friends. Then we uncover the gaps where revenue leaks
                    out of your funnel.
                  </p>
                  <div className="d-flex align-items-center text-success small">
                    <CheckCircleFill className="me-2" />
                    Churn, LTV, referrals, and loyalty baseline
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="light" text="dark" className="mb-2">
                    Step 2
                  </Badge>
                  <h5 className="fw-bold mb-2">
                    Strategy & Playbook Design
                  </h5>
                  <p className="text-muted small">
                    Together we build a practical playbook: loyalty programs,
                    communication cadences, offers, and referral systems that
                    match your business model.
                  </p>
                  <div className="d-flex align-items-center text-success small">
                    <CheckCircleFill className="me-2" />
                    Clear 30-, 60-, and 90-day priorities
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <Badge bg="light" text="dark" className="mb-2">
                    Step 3
                  </Badge>
                  <h5 className="fw-bold mb-2">Implementation & Systems</h5>
                  <p className="text-muted small">
                    We help you implement the right mix of tools, automations,
                    and human systems so your team can deliver a consistent
                    customer experience.
                  </p>
                  <div className="d-flex align-items-center text-success small">
                    <CheckCircleFill className="me-2" />
                    Measurable improvements and dashboards
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-5 bg-light">
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 className="fw-bold">Consulting Services That Drive CLV</h2>
              <p className="text-muted">
                These are the high-leverage areas where we typically start.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <BarChart className="mb-2" size={28} />
                  <h6 className="fw-bold">Customer Acquisition Strategy</h6>
                  <p className="small text-muted mb-2">
                    Attract the right customers, not just more of them, with
                    clearer offers and targeting.
                  </p>
                  <ul className="small text-muted mb-0 ps-3">
                    <li>Ideal customer profile (ICP)</li>
                    <li>Messaging & offer refinement</li>
                    <li>Acquisition channel optimization</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <StarFill className="mb-2" size={28} />
                  <h6 className="fw-bold">Loyalty & Rewards Design</h6>
                  <p className="small text-muted mb-2">
                    Create loyalty programs customers actually use — and that
                    pay for themselves.
                  </p>
                  <ul className="small text-muted mb-0 ps-3">
                    <li>Points, tiers & perks structure</li>
                    <li>Birthday & anniversary campaigns</li>
                    <li>Re-activation for “sleepy” customers</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <ChatDots className="mb-2" size={28} />
                  <h6 className="fw-bold">Customer Journeys & Communication</h6>
                  <p className="small text-muted mb-2">
                    Map every touchpoint from first contact to long-term
                    advocate and remove friction.
                  </p>
                  <ul className="small text-muted mb-0 ps-3">
                    <li>Onboarding & welcome flows</li>
                    <li>Retention & win-back campaigns</li>
                    <li>Feedback & review systems</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6} lg={3}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <ShieldCheck className="mb-2" size={28} />
                  <h6 className="fw-bold">Systems, Tools & Automation</h6>
                  <p className="small text-muted mb-2">
                    Connect your CRM, loyalty apps, and communication tools into
                    a single customer engine.
                  </p>
                  <ul className="small text-muted mb-0 ps-3">
                    <li>Tech stack assessment</li>
                    <li>Automation workflows</li>
                    <li>Reporting & dashboards</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* SYSTEMS & TOOLS */}
      <section id="systems" className="py-5">
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 className="fw-bold">Systems & Tools We Help You Build</h2>
              <p className="text-muted">
                Choose a few quick wins or build a complete lifetime customer
                ecosystem.
              </p>
            </Col>
          </Row>
          <Row className="g-4">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold mb-3">Customer Loyalty Programs</h5>
                  <Row>
                    <Col md={6}>
                      <ul className="small text-muted ps-3 mb-0">
                        <li>Points-based loyalty system</li>
                        <li>Tiered VIP programs</li>
                        <li>Visit-based punch cards</li>
                        <li>Birthday & special date offers</li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <ul className="small text-muted ps-3 mb-0">
                        <li>Win-back campaigns for inactive customers</li>
                        <li>In-store & online tracking options</li>
                        <li>Reporting on repeat visit rates</li>
                      </ul>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold mb-3">Referral & Review Engines</h5>
                  <Row>
                    <Col md={6}>
                      <ul className="small text-muted ps-3 mb-0">
                        <li>Automated referral invitations</li>
                        <li>Reward structures that motivate</li>
                        <li>Review request sequences</li>
                      </ul>
                    </Col>
                    <Col md={6}>
                      <ul className="small text-muted ps-3 mb-0">
                        <li>Feedback loops to fix issues fast</li>
                        <li>Social proof & testimonial systems</li>
                        <li>NPS & satisfaction surveys</li>
                      </ul>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
          <Row className="g-4 mt-3">
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold mb-3">Churn & Retention Alerts</h5>
                  <ul className="small text-muted ps-3 mb-0">
                    <li>Define “at-risk” customer signals</li>
                    <li>Automated alerts for your team</li>
                    <li>Save-the-customer outreach scripts</li>
                    <li>Retention-focused offers & bundles</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h5 className="fw-bold mb-3">
                    Customer Lifetime Value Dashboards
                  </h5>
                  <ul className="small text-muted ps-3 mb-0">
                    <li>Track CLV, churn, and repeat purchase rate</li>
                    <li>Segment best customers & VIPs</li>
                    <li>Identify top offers and channels</li>
                    <li>Executive-friendly reporting views</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* RESULTS / SOCIAL PROOF */}
      <section id="results" className="py-5 bg-light">
        <Container>
          <Row className="mb-4 text-center">
            <Col>
              <h2 className="fw-bold">What Kind of Results Can You Expect?</h2>
              <p className="text-muted">
                Every business is different, but here’s what we typically see
                after implementing TrueLifetimeCustomer systems.
              </p>
            </Col>
          </Row>
          <Row className="g-4 mb-4 text-center">
            <Col md={4}>
              <h3 className="fw-bold mb-0">+25–60%</h3>
              <p className="small text-muted mb-0">
                Increase in repeat purchase rate
              </p>
            </Col>
            <Col md={4}>
              <h3 className="fw-bold mb-0">+15–40%</h3>
              <p className="small text-muted mb-0">
                Higher average customer lifetime value
              </p>
            </Col>
            <Col md={4}>
              <h3 className="fw-bold mb-0">2–5x</h3>
              <p className="small text-muted mb-0">
                More reviews and word-of-mouth referrals
              </p>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col md={8}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex flex-column flex-md-row align-items-md-center">
                    <div className="me-md-3 mb-3 mb-md-0">
                      <div className="d-flex align-items-center mb-1">
                        <StarFill className="text-warning me-1" />
                        <StarFill className="text-warning me-1" />
                        <StarFill className="text-warning me-1" />
                        <StarFill className="text-warning me-1" />
                        <StarFill className="text-warning me-1" />
                      </div>
                      <h6 className="fw-bold mb-1">
                        “Our customers stopped disappearing.”
                      </h6>
                      <p className="small text-muted mb-0">
                        “We thought we just needed more leads. TrueLifetimeCustomer
                        showed us how much money we were leaving on the table by
                        not nurturing the customers we already had. Within 90
                        days, repeat business became our main growth engine.”
                      </p>
                    </div>
                    <div className="ms-md-auto text-md-end">
                      <p className="small fw-bold mb-0">
                        Alex R. – Multi-Location Service Business
                      </p>
                      <p className="small text-muted mb-0">
                        32% higher CLV in 12 months
                      </p>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA / CONSULTATION */}
      <section id="consult" className="py-5">
        <Container>
          <Row className="align-items-center g-4">
            <Col md={7}>
              <h2 className="fw-bold mb-3">
                Ready to Build Your True Lifetime Customer Engine?
              </h2>
              <p className="text-muted">
                Whether you’re just starting to think about a loyalty program or
                you want a complete customer growth system, we’ll meet you
                where you are. Start with a free, no-pressure 30-minute
                strategy session.
              </p>
              <ul className="small text-muted ps-3 mb-3">
                <li>Review your current customer journey and key numbers</li>
                <li>Identify 2–3 quick wins you can implement immediately</li>
                <li>Map out a 90-day True Lifetime Customer Roadmap</li>
              </ul>
              <Button
                href="#top"
                variant="success"
                className="d-flex align-items-center"
              >
                Start My Free Customer Growth Plan
                <ArrowRight className="ms-2" />
              </Button>
              <p className="small text-muted mt-2 mb-0">
                Perfect for: local service businesses, retail, e-commerce,
                professional services, membership & subscription models.
              </p>
            </Col>
            <Col md={5}>
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <h6 className="fw-bold mb-2">What you’ll walk away with:</h6>
                  <ul className="small text-muted ps-3 mb-0">
                    <li>CLV & retention opportunities identified</li>
                    <li>Suggested loyalty / referral / review systems</li>
                    <li>Tooling & automation recommendations</li>
                    <li>A simple action plan you can execute</li>
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FOOTER */}
      <footer className="py-4 bg-dark text-light">
        <Container>
          <Row className="align-items-center">
            <Col md={6} className="mb-3 mb-md-0">
              <div className="fw-bold">
                TrueLifetimeCustomer
                <span className="text-success">.com</span>
              </div>
              <div className="small text-muted">
                Helping you turn customers into long-term relationships.
              </div>
            </Col>
            <Col
              md={6}
              className="d-flex justify-content-md-end flex-wrap gap-3 small"
            >
              <a href="#services" className="text-decoration-none text-muted">
                Services
              </a>
              <a href="#systems" className="text-decoration-none text-muted">
                Systems &amp; Tools
              </a>
              <a href="#consult" className="text-decoration-none text-muted">
                Get a Plan
              </a>
              <span className="text-muted">
                © {new Date().getFullYear()} TrueLifetimeCustomer. All rights
                reserved.
              </span>
            </Col>
          </Row>
        </Container>
      </footer>
    </>
  );
};

export default TrueLifetimeCustomerLandingPage_2025_11_30_chat;
